import { useEffect, useState } from 'react'

import {
    Box,
    Typography,
    Divider,
    useMediaQuery,
    useTheme,
} from '@material-ui/core'

import { useHistory, useLocation } from 'react-router-dom'

import { POSCard, POSHeaderStepper, POSVerifyRouteAccess } from 'src/components'

import {
    useMainContext,
    useTrackerContext,
    IResult,
    useCreatePurchaseV2,
    useQuery,
} from 'src/hooks'

import { formatCurrency, onlyNumbers } from 'src/utils'

import { paths, Platform } from 'src/config/routes-paths'

import { logError } from 'src/config/log-error'

import { PaymentMethodType } from 'src/types'

import { BoxInfo } from './BoxInfo'
import { Contract, SubmitContract } from './Contract'
import { createCardToken } from 'src/utils/create-new-payment-profile'
import { ICredCardParams } from 'src/utils/types/credit-card'
import { useHubspot } from 'src/hooks/use-hubspot'

const getValueOr = (value: any, defaultValue: string | number = '') =>
    value || defaultValue
const getLastFourDigits = (digits = '') => digits.substr(digits.length - 4)

export enum ErrorPurchase {
    INVALID_CUSTOMER_INFO = 'INVALID_CUSTOMER_INFO',
    INVALID_PAYMENT_INFO = 'INVALID_PAYMENT_INFO',
    INVALID_COUPON = 'INVALID_COUPON',
    FAILED_PAYMENT_PROFILE = 'FAILED_PAYMENT_PROFILE',
    FAILED_CUSTOMER_PROFILE = 'FAILED_CUSTOMER_PROFILE',
    MISMATCH_PRODUCT = 'MISMATCH_PRODUCT',
    INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
    INVALID_CARD_BRAND = 'INVALID_CARD_BRAND',
}

const getErrorStep = (error: ErrorPurchase) => {
    switch (error) {
        case ErrorPurchase.MISMATCH_PRODUCT:
        case ErrorPurchase.INTERNAL_SERVER_ERROR:
            return paths.initial
        case ErrorPurchase.INVALID_CUSTOMER_INFO:
        case ErrorPurchase.FAILED_CUSTOMER_PROFILE:
            return paths['personal-data']
        case ErrorPurchase.INVALID_PAYMENT_INFO:
        case ErrorPurchase.INVALID_COUPON:
        case ErrorPurchase.FAILED_PAYMENT_PROFILE:
        case ErrorPurchase.INVALID_CARD_BRAND:
            return paths.payment
        default:
            return paths.initial
    }
}

const Review = () => {
    const theme = useTheme()
    const matches = useMediaQuery(theme.breakpoints.down('sm'))
    const history = useHistory()
    const location = useLocation()
    const platform = location.pathname.split('/')[2] as Platform

    const query = useQuery()
    const { sendPaymentForm } = useHubspot(query, platform)

    const [isSubmitted, setSubmitted] = useState(false)

    const {
        buyer,
        productSelected,
        data,
        setBuyer,
        goToPayment,
        contract,
        isPromoEnabled,
        isRecurrenceEnabled,
        isBankSlipEnabled,
        totalValueWithCoupon,
        installmentValueWithCoupon,
        isSanarPlus,
    } = useMainContext()

    const { mutateAsync: createPurchaseV2 } = useCreatePurchaseV2()
    const validatePaymentMethod = isSanarPlus
        ? PaymentMethodType.CREDIT_CARD
        : buyer?.paymentMethod

    const effectiveInstallments = isSanarPlus
        ? 1
        : getValueOr(productSelected?.totalInstallments, 1)

    const rawTotal = isSanarPlus
        ? Number(data?.price ?? 0)
        : Number(productSelected?.totalValueToPay ?? 0)

    const totalPriceValue = data.coupon?.promoCode
        ? Number(totalValueWithCoupon ?? rawTotal)
        : rawTotal

    const totalPriceFormatted = formatCurrency.format(totalPriceValue)

    const rawPerInstallment = isSanarPlus
        ? Number(data?.price ?? 0)
        : Number(productSelected?.installmentPrice ?? 0)

    const perInstallmentValue = data.coupon?.promoCode
        ? Number(installmentValueWithCoupon ?? rawPerInstallment)
        : rawPerInstallment

    const installmentsToShow = isSanarPlus
        ? 1
        : isPromoEnabled &&
          !productSelected?.isFirstChargeReductionCampaign &&
          !isRecurrenceEnabled &&
          (productSelected?.totalInstallments ?? 0) > 1 &&
          productSelected?.paymentMethod === PaymentMethodType.CREDIT_CARD
        ? (productSelected?.totalInstallments ?? 0) - 1
        : productSelected?.totalInstallments ?? 1

    const billingDay =
        !!buyer?.billingDay && buyer?.billingDay !== ''
            ? Number(buyer?.billingDay)
            : undefined

    const { track } = useTrackerContext()

    useEffect(() => {
        if (!!data?.id) {
            track({
                eventName: 'Purchase summary page viewed',
                additionalAttrs: {
                    'Payment method': isSanarPlus
                        ? 'Cartão de crédito'
                        : buyer?.paymentMethod === PaymentMethodType.BANK_SLIP
                        ? 'Boleto'
                        : 'Cartão de crédito',
                    'Course name': data?.name,
                    'Course duration': `${data?.duration} meses`,
                },
            })
        }
    }, [
        track,
        data?.name,
        data?.id,
        data?.duration,
        buyer?.paymentMethod,
        isSanarPlus,
    ])

    useEffect(() => {
        if (
            isSanarPlus &&
            buyer?.paymentMethod !== PaymentMethodType.CREDIT_CARD
        ) {
            setBuyer((prev) => ({
                ...prev,
                paymentMethod: PaymentMethodType.CREDIT_CARD,
            }))
        }
    }, [isSanarPlus, buyer?.paymentMethod, setBuyer])

    useEffect(() => {
        if (!isSubmitted && !contract?.id) {
            history.replace(paths.contractRegularization)
        }
    }, [isSubmitted, contract?.id, history])

    const handleBack = () => {
        track({
            eventName: 'Review purchase CTA clicked',
            additionalAttrs: {
                'Payment method': isSanarPlus
                    ? 'Cartão de crédito'
                    : buyer?.paymentMethod === PaymentMethodType.BANK_SLIP
                    ? 'Boleto'
                    : 'Cartão de crédito',
                'Course name': data?.name,
                'Course duration': `${data?.duration} meses`,
            },
        })
        goToPayment()
    }

    const goToErrorPage = (error: ErrorPurchase) => {
        const stepError = getErrorStep(error)
        history.push({
            pathname: `./${paths.error}`,
            search: history.location.search
                ? history.location.search + `&step=${stepError}`
                : `?step=${stepError}`,
        })
    }

    const goToSuccessPage = (dataResult: IResult) => {
        if (validatePaymentMethod === PaymentMethodType.BANK_SLIP) {
            const params = new URLSearchParams(history.location.search)
            params.append('code', dataResult?.typableBarcode)
            params.append('url', dataResult?.bankSlipUrl)
            history.push({
                pathname: `./${paths.success}`,
                search: `?${params.toString()}`,
            })
        } else {
            history.push(`./${paths.success}` + history.location.search)
        }
    }

    const getCardTokenSafe = async (): Promise<string> => {
        try {
            return await createCardToken({
                card_cvv: buyer?.creditCard?.cvv!,
                card_expiration: buyer?.creditCard?.expirationDate!,
                card_number: buyer?.creditCard?.creditCard!,
                holder_name: buyer?.creditCard?.name!,
                payment_company_code: buyer?.creditCard?.creditCardBrand!,
            } as ICredCardParams)
        } catch (error: any) {
            logError('FAILED_CREATE_PAYMENT_PROFILE', {
                message: error?.message,
            })
            goToErrorPage(ErrorPurchase.INVALID_CARD_BRAND)
            return ''
        }
    }

    const successPurchase = (result: IResult) => {
        setSubmitted(true)

        setBuyer((prev) => ({
            phone: prev?.phone,
            email: prev?.email,
            paymentMethod: prev?.paymentMethod,
            rg: buyer?.address?.rg!,
            birthdate: buyer?.address?.birthdate!,
            gender: buyer?.address?.gender!,
            countryCode: buyer?.countryCode!,
        }))

        const cls = data?.courseClasses?.[0]
        const startDate = cls?.startDate
            ? String(cls.startDate)
            : new Date().toISOString()
        const endDate = cls?.endDate
            ? String(cls.endDate)
            : new Date().toISOString()
        const className = cls?.name ?? data?.name ?? ''

        sendPaymentForm({
            email: buyer?.email!,
            course: data.name,
            endDate,
            startDate,
            paymentMethod:
                validatePaymentMethod === PaymentMethodType.BANK_SLIP
                    ? 'Boleto'
                    : 'Cartão de crédito',
            address: `Rua ${buyer?.address?.street}, N ${buyer?.address?.number}, Bairro ${buyer?.address?.neighborhood}. Complemento: ${buyer?.address?.complement}`,
            city: buyer?.address?.city!,
            zip: buyer?.address?.zipCode!,
            state: buyer?.address?.state!,
            amount: String(rawTotal || ''),
            class: className,
            cpf: buyer?.cpf!,
            orderId: result?.orderId,
        })

        goToSuccessPage(result)
    }

    const failPurchase = (response: any) => {
        setSubmitted(true)
        const resp = response?.data ?? {}

        setBuyer((prev) => ({ ...prev, creditCard: undefined, cpf: undefined }))

        logError(`FAILED_CREATE_PURCHASE`, {
            message: resp?.message,
            error: resp?.error,
            productId: productSelected?.id!,
            promoCode: resp?.coupon?.promoCode!,
            paymentMethod: buyer?.paymentMethod!,
            status: response?.status,
        })

        goToErrorPage(resp?.error || ErrorPurchase.INTERNAL_SERVER_ERROR)
    }

    const submitPayments: SubmitContract = async ({ acceptTerms }) => {
        if (!contract?.id) {
            history.replace(paths.contractRegularization)
            return
        }

        let cardToken = ''
        if (validatePaymentMethod === PaymentMethodType.CREDIT_CARD) {
            cardToken = await getCardTokenSafe()
            if (cardToken === '') return
        }

        const planSlugToSend = productSelected?.planSlug ?? data?.planSlug!
        const productSlugToSend =
            data?.courseClasses?.[0]?.productSlug ?? data?.productSlug!

        try {
            const payload = {
                acceptTerms: !!acceptTerms,
                platform: isSanarPlus ? 'sanarplus' : 'posmed',
                user: {
                    name: buyer?.name!,
                    email: buyer?.email!,
                    phone: {
                        number: onlyNumbers(buyer?.phone!),
                        countryCode: buyer?.countryCode ?? '+55',
                    },
                    cpf: onlyNumbers(buyer?.cpf!),
                    rg: buyer?.address?.rg!,
                    birthdate: buyer?.address?.birthdate!,
                    gender: buyer?.address?.gender!,
                    address: {
                        country: 'BR',
                        state: buyer?.address?.state!,
                        city: buyer?.address?.city!,
                        neighborhood: buyer?.address?.neighborhood!,
                        street: buyer?.address?.street!,
                        zipCode: onlyNumbers(buyer?.address?.zipCode!),
                        number: String(buyer?.address?.number),
                        complement: buyer?.address?.complement,
                    },
                },
                paymentMethod: validatePaymentMethod!,
                paymentToken: cardToken,
                planSlug: planSlugToSend,
                promoCode: data?.coupon?.promoCode!,
                productSlug: productSlugToSend,
                billingDay,
                contractId: contract.id,
            }

            const result = await createPurchaseV2(payload)
            successPurchase(result)
        } catch (e: any) {
            const resp = e?.response ?? {
                data: { error: ErrorPurchase.INTERNAL_SERVER_ERROR },
            }
            failPurchase(resp)
        }
    }

    const billingDayInfo = billingDay
        ? [
              {
                  label: 'Dia da cobrança (a partir da próxima cobrança)',
                  value: String(billingDay),
              },
          ]
        : []

    return (
        <Box mb={{ xs: 8, md: 12 }}>
            {!isSubmitted && (
                <POSVerifyRouteAccess
                    condition={
                        !buyer?.address ||
                        !buyer.name ||
                        !buyer.email ||
                        !buyer.phone ||
                        !validatePaymentMethod ||
                        !buyer.cpf
                    }
                />
            )}

            <POSHeaderStepper courseName={data?.name || ''} activeStep={3} />

            <Typography variant="body1" paragraph color="textSecondary">
                Revise sua compra:
            </Typography>
            <POSCard p={{ xs: 2, md: 4 }}>
                <POSHeaderStepper courseName={data?.name || ''} size="small" />
                <Box
                    display="flex"
                    justifyContent="space-between"
                    flexDirection={{ xs: 'column', md: 'row' }}
                    mt={4}
                >
                    <BoxInfo
                        title="Dados Pessoais"
                        infos={[
                            { label: 'Nome', value: getValueOr(buyer?.name) },
                            {
                                label: 'E-mail',
                                value: getValueOr(buyer?.email),
                            },
                            {
                                label: 'Telefone celular',
                                value: `${getValueOr(
                                    buyer?.countryCode
                                )} ${getValueOr(buyer?.phone)}`,
                            },
                        ]}
                    />
                    <Box mx={{ xs: 0, md: 8 }} my={{ xs: 2, md: 0 }}>
                        <Divider
                            orientation={matches ? 'horizontal' : 'vertical'}
                        />
                    </Box>
                    <BoxInfo
                        title="Endereço"
                        infos={[
                            {
                                label: 'CEP',
                                value: getValueOr(buyer?.address?.zipCode),
                            },
                            {
                                label: 'Endereço',
                                value: getValueOr(buyer?.address?.street),
                            },
                            {
                                label: 'Número',
                                value: getValueOr(buyer?.address?.number),
                            },
                            {
                                label: 'Complemento',
                                value: getValueOr(buyer?.address?.complement),
                            },
                            {
                                label: 'Bairro',
                                value: getValueOr(buyer?.address?.neighborhood),
                            },
                            {
                                label: 'Cidade',
                                value: getValueOr(buyer?.address?.city),
                            },
                            {
                                label: 'Estado',
                                value: getValueOr(buyer?.address?.state),
                            },
                        ]}
                    />
                    <Box mx={{ xs: 0, md: 8 }} my={{ xs: 2, md: 0 }}>
                        <Divider
                            orientation={matches ? 'horizontal' : 'vertical'}
                        />
                    </Box>

                    <Box flex={1}>
                        {validatePaymentMethod ===
                        PaymentMethodType.CREDIT_CARD ? (
                            <BoxInfo
                                title="Dados de Pagamento"
                                infos={[
                                    {
                                        label: 'Cartão de Crédito',
                                        value: `xxxx.xxxx.xxxx.${getLastFourDigits(
                                            buyer?.creditCard?.creditCard
                                        )}`,
                                    },
                                    {
                                        label: 'Bandeira do Cartão',
                                        value: getValueOr(
                                            buyer?.creditCard?.creditCardBrand
                                        ),
                                    },
                                    {
                                        label: 'Nome do titular',
                                        value: getValueOr(
                                            buyer?.creditCard?.name
                                        ),
                                    },
                                    {
                                        label: 'Vencimento',
                                        value: getValueOr(
                                            buyer?.creditCard?.expirationDate
                                        ),
                                    },
                                    ...billingDayInfo,
                                ]}
                                mb={4}
                            />
                        ) : (
                            <BoxInfo
                                title="Dados de Pagamento"
                                infos={[
                                    { label: 'Boleto Bancário' },
                                    {
                                        label: 'Parcelas',
                                        value: String(effectiveInstallments),
                                    },
                                    ...billingDayInfo,
                                ]}
                                mb={4}
                            />
                        )}

                        <BoxInfo
                            title="Seu Investimento"
                            infos={[
                                { label: 'Total', value: totalPriceFormatted },
                            ]}
                        >
                            {!isSanarPlus &&
                                !isBankSlipEnabled &&
                                isPromoEnabled &&
                                productSelected?.priceFirstInstallment &&
                                productSelected?.paymentMethod ===
                                    PaymentMethodType.CREDIT_CARD && (
                                    <Box display={'flex'}>
                                        <Typography
                                            style={{
                                                color: '#00297B',
                                                fontSize: '16px',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {`1ª parcela de `}
                                        </Typography>
                                        &nbsp;
                                        <Typography
                                            style={{
                                                color: '#2CAA6E',
                                                fontSize: '16px',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {` ${formatCurrency.format(
                                                productSelected?.priceFirstInstallment
                                            )} + `}
                                        </Typography>
                                    </Box>
                                )}

                            <Typography
                                color="secondary"
                                variant="h6"
                                style={{ fontWeight: 700 }}
                            >
                                {`${installmentsToShow}x ${formatCurrency.format(
                                    perInstallmentValue
                                )}`}
                            </Typography>
                        </BoxInfo>
                    </Box>
                </Box>
            </POSCard>

            <Contract onSubmit={submitPayments} onBack={handleBack} />
        </Box>
    )
}

export default Review
